import type { PromptInputMessage } from '@/components/ai-elements/prompt-input';
import { AskAiMobileComposer } from '@/features/assistant/_components/ask-ai-mobile-composer';
import { useCallback, useRef } from 'react';

interface ConversationMobileComposerProps {
  draft: string;
  onDraftChange: (draft: string) => void;
  onSend: (content: string) => Promise<unknown>;
  disabled?: boolean;
  isSending?: boolean;
  placeholder?: string;
  onLayoutChange?: () => void;
}

/**
 * Mobile inbox composer — same glass pill UI/behavior as the Ask AI sheet.
 */
export function ConversationMobileComposer({
  onSend,
  draft,
  onDraftChange,
  disabled = false,
  isSending = false,
  placeholder = 'Message',
  onLayoutChange,
}: ConversationMobileComposerProps) {
  const submitting = useRef(false);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      const text = message.text?.trim();
      if (!text || disabled || isSending || submitting.current) {
        return;
      }
      submitting.current = true;
      try {
        await onSend(text);
        onDraftChange('');
      } catch {
        // The send hook shows the error. Keep the draft for staff to review.
      } finally {
        submitting.current = false;
      }
    },
    [disabled, isSending, onSend, onDraftChange]
  );

  return (
    <div className="sticky bottom-0 z-20 shrink-0 overflow-visible bg-transparent px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1.5">
      <AskAiMobileComposer
        draft={draft}
        onDraftChange={onDraftChange}
        onSubmit={handleSubmit}
        onStop={() => undefined}
        status="ready"
        placeholder={placeholder}
        disabled={disabled || isSending}
        submitDisabled={!draft.trim() || disabled || isSending}
        onAttachClick={() => undefined}
        onLayoutChange={onLayoutChange}
        compactPadding
      />
    </div>
  );
}
